import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import SelectNew from "components/select/SelectNew";
import Sheet from "components/sheet/Sheet";
import TimePick from "components/timePick/TimePick";
import Portal from "elements/Portal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./eventsCalendar.scss";
import Checkbox from "components/form/checkbox/Checkbox";
import moment from "moment";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import axios, { CancelToken } from "axios";
import { GetTeeSheetTemplate, ITeeSheetTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/teesheetTemplate";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { formatDate } from "helpers/Helpers";
import { TEventApplication } from "api/rpc/2024-04/facilityAdmin/event/eventApplication";
import ReactDOM from "react-dom";
import { IBookingCategory } from "redux/reducers/models/teetime";
import { GetBookingCategory } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { TShotgunTemplate } from "redux/reducers/models/teesheet";
import { GetShotGunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";

interface IProps {
  open: boolean;
  onClose: () => void;
  onOk: (state: IApplicationParams, type: "new" | "edit", shotgun: boolean) => Promise<boolean>;
  startPage?: "tee-sheet" | "template";
  application?: TEventApplication;
  tournamentType?: "regular" | "shotgun" | "crossover";
}

interface IState {
  course_id: number;
  division_id: number;
  start_date: Date;
  end_date: Date;
  start_time: string;
  end_time: string;
  recurrence_type: "does_not_repeat" | "daily" | "weekly" | "monthly" | "annually" | "every_week_day" | "custom";
  repeat_amount: string;

  interval: "day" | "week" | "month" | "year";
  //interval 'week' selected
  weekdays: Array<number>;
  //interval 'month' selected
  monthly_rule_type: "day_of_month" | "nth_weekday";

  ends_type: "" | "on" | "after";
  //ends_type 'on' selected
  ends_on_date: Date;
  //ends_type 'after' selected
  ends_after_occurrences: string;

  template_id: number;

  page: "tee-sheet" | "template";

  //shotgun
  turn_division_id: number;
  duration: string;
  leadTime: string;
  bookingCategoryIds: Array<number>;
  shotgunTemplateId: number;
}

export interface IApplicationParams {
  course_id: number;
  division_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  recurrence_type: "does_not_repeat" | "daily" | "weekly" | "monthly" | "annually" | "every_week_day" | "custom";
  repeat_amount: string;

  interval: "day" | "week" | "month" | "year";
  //interval 'week' selected
  weekdays: Array<number>;
  //interval 'month' selected
  monthly_rule_type: "day_of_month" | "nth_weekday";

  ends_type: "" | "on" | "after";
  //ends_type 'on' selected
  ends_on_date: string;
  //ends_type 'after' selected
  ends_after_occurrences: string;

  template_id: number;
  page: "tee-sheet" | "template";

  //shotgun
  turn_division_id: number;
  duration: string;
  leadTime: string;
  bookingCategoryIds: Array<number>;
  shotgunTemplateId: number;
}

export default function ScheduleEventModal(props: IProps) {
  const { open, onClose, onOk, startPage, application } = props;
  const { t } = useTranslation();
  const afterInputRef = useRef<HTMLInputElement>();
  const dispatch = useAppDispatch();
  const [templates, setTemplates] = useState<Array<ITeeSheetTemplate>>(undefined);
  const { facilityStore } = useAppSelector(store => store);
  const [isShotgun, setIsShotgun] = useState<boolean>(false);
  const [nineHoleSide, setNineHoleSide] = useState<"front" | "back" | null>(null);
  const [bookingCategories, setBookingCategories] = useState<Array<IBookingCategory>>(undefined);
  const [shotgunTemplates, setShotgunTemplates] = useState<Array<TShotgunTemplate>>(undefined);

  const [state, setState] = useState<IState>({
    division_id: null,
    course_id: null,
    start_date: new Date(),
    end_date: new Date(),
    start_time: "09:00",
    end_time: "12:00",
    recurrence_type: "does_not_repeat",
    repeat_amount: "",
    interval: "day",
    ends_type: "",
    ends_on_date: new Date(),
    weekdays: [],
    monthly_rule_type: "day_of_month",
    ends_after_occurrences: "",
    template_id: null,
    page: "tee-sheet",

    //shotgun
    turn_division_id: null,
    duration: "",
    leadTime: "",
    bookingCategoryIds: [],
    shotgunTemplateId: null,
  });

  const [renderTimePick, setRenderTimePick] = useState(false);

  useEffect(() => {
    if (facilityStore?.facility && facilityStore?.facility?.courses?.length > 0) {
      setState(prev => ({ ...prev, course_id: facilityStore?.facility?.courses[0]?.id }));
    }
  }, [facilityStore?.facility?.courses]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (open && startPage) {
      setState(prev => ({ ...prev, page: startPage }));
      if (templates === undefined) {
        void loadTeeSheetTemplates(source.token);
      }
      if (!bookingCategories) {
        void loadBookingCategories(source.token);
      }
      if (!shotgunTemplates) {
        void loadShotgunTemplates(source.token);
      }
    }
    return () => source.cancel();
  }, [open, startPage]);

  useEffect(() => {
    if (application) {
      ReactDOM.unstable_batchedUpdates(() => {
        setState({
          division_id: application?.division_id,
          course_id: application?.course_id,
          start_date: application?.start_date
            ? new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())
            : new Date(),
          end_date: application?.end_date
            ? new Date(moment(application?.end_date, "YYYY-MM-DD")?.toISOString())
            : new Date(),
          start_time: application?.start_time ? application?.start_time : "09:00",
          end_time: application?.end_time ? application?.end_time : "12:00",
          recurrence_type: application?.recurrence_type ? application?.recurrence_type : "does_not_repeat",
          repeat_amount: application?.repeat_amount ? application?.repeat_amount?.toString() : "",
          interval: application?.interval ? application?.interval : "day",
          ends_type: application?.ends_type ? application?.ends_type : "",
          ends_on_date: application?.ends_on_date
            ? new Date(moment(application?.ends_on_date, "YYYY-MM-DD")?.toISOString())
            : new Date(),
          weekdays: application?.weekdays ? application?.weekdays : [],
          monthly_rule_type: application?.monthly_rule_type ? application?.monthly_rule_type : "day_of_month",
          ends_after_occurrences: application?.ends_after_occurrences
            ? application?.ends_after_occurrences?.toString()
            : "",
          template_id: application?.template_id ? application?.template_id : null,
          page: application?.template_id ? "template" : "tee-sheet",
          turn_division_id: null,
          duration: "",
          leadTime: "",
          bookingCategoryIds: [],
          shotgunTemplateId: null,
        });
        setRenderTimePick(true);
      });
    }
  }, [application?.id]);

  // Auto focus input on checkbox select
  useEffect(() => {
    if (state.ends_type === "after") {
      afterInputRef.current?.focus();
    }
  }, [state.ends_type]);

  async function loadBookingCategories(token?: CancelToken) {
    const res = await GetBookingCategory(null, false, token);
    if (res?.status !== StatusCode.OK) {
      if (token && token?.reason) {
        return;
      }
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting booking categories")));
      setBookingCategories([]);
      return;
    }
    setBookingCategories(res?.data);
  }

  async function loadShotgunTemplates(token?: CancelToken) {
    const shotgunTemplateRes = await GetShotGunTemplate(null, false, token);
    if (shotgunTemplateRes?.status !== StatusCode.OK) {
      if (token && token?.reason) {
        return;
      }
      dispatch(showError(t("Error getting shotgun templates")));
      setShotgunTemplates([]);
      return;
    }
    setShotgunTemplates(shotgunTemplateRes?.data as Array<TShotgunTemplate>);
  }

  function parseShotgunHoleNumber(startHole: string): number {
    const match = startHole.match(/^(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  async function loadSelectedShotgunTemplateMetadata(templateId: number) {
    // Reset first so the previous template's side does not affect the new selection.
    setNineHoleSide(null);

    if (!templateId) {
      return;
    }

    const templateRes = await GetShotGunTemplate({ id: templateId, extended: true }, false);
    if (templateRes.status !== StatusCode.OK) {
      return;
    }

    const teeTimes: Array<{ start_hole: string; position?: number }> =
      templateRes.data?.[0]?.tee_times ?? [];
    const holes = [...teeTimes]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(teeTime => parseShotgunHoleNumber(teeTime.start_hole))
      .filter(hole => hole > 0);

    if (holes.length === 0) {
      return;
    }

    // Reverse shotguns can span both nines; leave both division selectors available.
    const isReverseShotgun = holes.length > 1 && holes[0] > holes[holes.length - 1];
    if (isReverseShotgun) {
      return;
    }

    const minHole = Math.min(...holes);
    const maxHole = Math.max(...holes);

    if (maxHole <= 9) {
      setNineHoleSide("front");
      setState(prev => ({ ...prev, turn_division_id: null }));
    } else if (minHole >= 10) {
      setNineHoleSide("back");
      setState(prev => ({ ...prev, division_id: null }));
    }
  }

  async function loadTeeSheetTemplates(token?: CancelToken) {
    const templateRes = await GetTeeSheetTemplate(null, false, token);
    if (templateRes.status !== StatusCode.OK) {
      if (token && token?.reason) {
        return;
      }
      setTemplates([]);
      dispatch(
        showError(typeof templateRes?.data === "string" ? templateRes?.data : t("Error getting tee sheet templates")),
      );
      return;
    }
    setTemplates(templateRes.data);
  }

  function resetState() {
    ReactDOM.unstable_batchedUpdates(() => {
      setState({
        division_id: null,
        course_id: facilityStore?.facility?.courses[0]?.id,
        start_date: new Date(),
        end_date: new Date(),
        start_time: "09:00",
        end_time: "12:00",
        recurrence_type: "does_not_repeat",
        repeat_amount: "",
        interval: "day",
        ends_type: "",
        ends_on_date: new Date(),
        weekdays: [],
        monthly_rule_type: "day_of_month",
        ends_after_occurrences: "",
        template_id: null,
        page: "tee-sheet",
        turn_division_id: null,
        duration: "",
        leadTime: "",
        bookingCategoryIds: [],
        shotgunTemplateId: null,
      });
      setRenderTimePick(false);
      setIsShotgun(false);
      setNineHoleSide(null);
    });
  }

  function handleClose() {
    void resetState();
    void onClose();
  }

  async function handleOk() {
    const values = {
      ...state,
      start_date: formatDate(state?.start_date),
      end_date: formatDate(state?.end_date),
      ends_on_date: formatDate(state?.ends_on_date),
    };
    const applicationComplete = await onOk(values, application ? "edit" : "new", isShotgun);
    if (applicationComplete) {
      void handleClose();
    }
  }

  function handleDayOfWeekSelection(dayNumber: number) {
    let updatedSelection = state.weekdays ? [...state.weekdays] : [];
    if (updatedSelection.includes(dayNumber)) {
      updatedSelection = updatedSelection?.filter(num => num !== dayNumber);
    } else {
      updatedSelection.push(dayNumber);
    }
    setState(prev => ({ ...prev, weekdays: updatedSelection }));
  }

  function disableOK() {
    if (!isShotgun && !state?.division_id) {
      return true;
    }

    if (isShotgun) {
      if (
        (nineHoleSide !== "back" && !state?.division_id) ||
        (nineHoleSide !== "front" && !state?.turn_division_id) ||
        !state?.duration ||
        !state?.leadTime ||
        !state?.shotgunTemplateId ||
        state?.bookingCategoryIds?.length === 0
      ) {
        return true;
      }
    }
    if (state?.recurrence_type !== "custom" && state?.recurrence_type !== "does_not_repeat") {
      if (state?.start_date > state?.end_date) {
        return true;
      }
    } else if (state?.recurrence_type === "custom") {
      if (state?.ends_type === "" || !state?.repeat_amount) {
        return true;
      } else if (
        state?.ends_type === "after" &&
        (!state?.ends_after_occurrences || Number(state?.ends_after_occurrences) < 0)
      ) {
        return true;
      } else if (state?.interval === "week" && state?.weekdays?.length === 0) {
        return true;
      }
    } else if (state?.page === "template") {
      if (!state?.template_id) {
        return true;
      }

    }
    return false;
  }

  function getNthWeekdayOfMonth(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const weekday = date.getDay(); // 0-6

    let count = 0;

    for (let d = 1; d <= date.getDate(); d++) {
      const current = new Date(year, month, d);
      if (current.getDay() === weekday) {
        count++;
      }
    }

    return count;
  }

  function getBookingCategoryNamesShotgun() {
    const bookingCategoryNames: string[] = [];
    for (let i = 0; i < state?.bookingCategoryIds?.length; i++) {
      bookingCategoryNames[i] = `${String(
        bookingCategories?.find(
          (bookingCategory: IBookingCategory) => bookingCategory.id === state?.bookingCategoryIds[i],
        )?.title,
      )}`;
    }
    if (bookingCategoryNames.length === 0) {
      return;
    }
    return bookingCategoryNames.join(", ");
  }

  function handleBookingCategoryChangeShotgun(value: number) {
    if (isNaN(value)) {
      return;
    }
    if (!state?.bookingCategoryIds?.find(bookingCategoryId => bookingCategoryId === value)) {
      setState(prevState => ({
        ...prevState,
        bookingCategoryIds: [...prevState.bookingCategoryIds, value],
      }));
    } else {
      const updatedBookingCategoryIds = state?.bookingCategoryIds?.filter(
        bookingCategoryId => bookingCategoryId !== value,
      );
      setState(prevState => ({ ...prevState, bookingCategoryIds: updatedBookingCategoryIds }));
    }
  }

  const weekIndex = getNthWeekdayOfMonth(state?.start_date);
  const ORDINALS = [t("first"), t("second"), t("third"), t("fourth"), t("last")];

  return (
    <Portal isMounted={open}>
      <Sheet
        open={open}
        title={state?.page === "tee-sheet" ? t("Apply to tee sheet") : t("Apply to tee sheet template")}
        supportText={
          state?.page === "tee-sheet"
            ? t("Select date and time range to apply the event on the live tee sheet. Add shotgun event if needed.")
            : t("Select a template, date and time range to apply the event on the template. Add shotgun event if needed.")
        }
        onCancel={handleClose}
        backDropCancel={false}
        onOk={handleOk}
        okText={t("Save changes")}
        onSecondary={() => setState(prev => ({ ...prev, page: prev?.page === "tee-sheet" ? "template" : "tee-sheet" }))}
        secondaryDisabled={!!application}
        secondaryText={state?.page === "tee-sheet" ? t("Switch to template") : t("Switch to tee sheet")}
        size="small"
        closable
        okDisabled={disableOK()}
        stacked
      >
        {isShotgun ? (
          <FormLayout>
            {state?.page === "template" ? (
              <FormLayout.Group>
                <SelectNew
                  id={"template"}
                  label={t("Tee sheet template")}
                  placeholder={t("Select template")}
                  onChange={templateId =>
                    setState(prev => ({
                      ...prev,
                      template_id: templateId,
                      course_id: templates?.find(template => template?.id === templateId)?.course_id,
                    }))
                  }
                  value={state.template_id}
                  isLoading={!templates}
                  options={templates?.map(template => ({ id: template.id, label: template.name }))}
                  disabled={!!application}
                />
              </FormLayout.Group>
            ) : (
              <FormLayout.Group>
                <DatePickerInput
                  months={1}
                  position="left"
                  label={t("Start date")}
                  startingDate={state.start_date}
                  setStartingDate={date => setState(prev => ({ ...prev, start_date: date as Date }))}
                  usePortal
                />
                {state?.recurrence_type === "custom" || state?.recurrence_type === "does_not_repeat" ? null : (
                  <DatePickerInput
                    months={1}
                    position="right"
                    label={t("End date")}
                    startingDate={state.end_date}
                    setStartingDate={date => setState(prev => ({ ...prev, end_date: date as Date }))}
                  />
                )}
              </FormLayout.Group>
            )}

            {!application || (application && renderTimePick) ? (
              <FormLayout.Group>
                <TimePick
                  label={t("Start time")}
                  value={state.start_time}
                  size="large"
                  onChange={time => setState(prev => ({ ...prev, start_time: time }))}
                />
              </FormLayout.Group>
            ) : null}
            <FormLayout.Group>
              <div className="ui-events-calendar-divider"></div>
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox label={t("Set as a shotgun event")} onChange={e => setIsShotgun(e.target?.checked)} checked={isShotgun} size="medium"/>
            </FormLayout.Group>
            <FormLayout.Group>
              <SelectNew
                id="shotgun_templates"
                label={t("Shotgun template")}
                value={state.shotgunTemplateId}
                options={shotgunTemplates?.map(template => {
                  return {
                    id: template.id,
                    label: template.title,
                  };
                })}
                onChange={value => {
                  setState(prev => ({
                    ...prev,
                    shotgunTemplateId: value,
                    division_id: null,
                    turn_division_id: null,
                  }));
                  void loadSelectedShotgunTemplateMetadata(value);
                }}
                placeholder={t("Select a template")}
              />
            </FormLayout.Group>
            {state?.course_id ? (
              <FormLayout.Group>
                {facilityStore?.facility?.courses?.length > 1 && state?.page === "tee-sheet" ? (
                  <SelectNew
                    id={"courses"}
                    label={t("Course")}
                    placeholder={t("Select course")}
                    onChange={courseId => setState(prev => ({ ...prev, course_id: courseId }))}
                    value={state.course_id}
                    isLoading={!facilityStore?.facility?.courses}
                    options={facilityStore?.facility?.courses?.map(course => ({
                      id: course.id,
                      label: course?.full_name,
                    }))}
                    disabled={!!application}
                    usePortal
                  />
                ) : null}
                {nineHoleSide !== "back" ? (
                  <SelectNew
                    id={"front_division"}
                    label={t("Front")}
                    placeholder={t("Select division")}
                    onChange={divisionId => setState(prev => ({ ...prev, division_id: divisionId }))}
                    value={state.division_id}
                    isLoading={!facilityStore?.facility?.courses}
                    options={facilityStore?.facility?.courses
                      ?.find(course => course.id === state?.course_id)
                      ?.divisions?.map(division => ({ id: division.id, label: division?.title }))}
                    disabled={!!application}
                    usePortal
                  />
                ) : null}
                {nineHoleSide !== "front" ? (
                  <SelectNew
                    id={"turn_division_id"}
                    label={t("Back")}
                    placeholder={t("Select division")}
                    onChange={divisionId => setState(prev => ({ ...prev, turn_division_id: divisionId }))}
                    value={state.turn_division_id}
                    isLoading={!facilityStore?.facility?.courses}
                    options={facilityStore?.facility?.courses
                      ?.find(course => course.id === state?.course_id)
                      ?.divisions?.map(division => ({ id: division.id, label: division?.title }))}
                    disabled={!!application}
                    usePortal
                  />
                ) : null}
              </FormLayout.Group>
            ) : null}
            <FormLayout.Group>
              <TimePick
                label={t("Duration")}
                value={state?.duration}
                onChange={time => setState(prev => ({ ...prev, duration: time }))}
                size="large"
                hideMeridiem={12}
              />
              <TimePick
                label={t("Lead time")}
                value={state?.leadTime}
                onChange={time => setState(prev => ({ ...prev, leadTime: time }))}
                size="large"
                hideMeridiem={12}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <SelectNew
                id="booking_categories"
                label={t("Booking categories")}
                multiValue={state.bookingCategoryIds}
                isLoading={!bookingCategories}
                options={bookingCategories?.map(bookingCategory => {
                  return {
                    id: bookingCategory.id,
                    label: bookingCategory.title,
                  };
                })}
                onChange={value => handleBookingCategoryChangeShotgun(value)}
                placeholder={getBookingCategoryNamesShotgun() ?? ""}
                usePortal
              />
            </FormLayout.Group>
            {state?.page === "tee-sheet" ? (
              <>
                <FormLayout.Group>
                  <div className="ui-events-calendar-divider"></div>
                </FormLayout.Group>
                <FormLayout.Group>
                  <SelectNew
                    id={"repeat_selection"}
                    label={t("")}
                    labelHidden
                    placeholder={""}
                    onChange={recurrence_type => setState(prev => ({ ...prev, recurrence_type: recurrence_type }))}
                    value={state.recurrence_type}
                    usePortal
                    options={[
                      { id: "does_not_repeat", label: t("Does not repeat") },
                      { id: "daily", label: t("Daily") },
                      {
                        id: "weekly",
                        label: t("Weekly on {{dayOfWeek}}", { dayOfWeek: moment(state?.start_date)?.format("dddd") }),
                      },
                      {
                        id: "monthly",
                        label: t("Monthly on first {{dayOfWeek}}", {
                          dayOfWeek: moment(state?.start_date)?.format("dddd"),
                        }),
                      },
                      {
                        id: "annually",
                        label: t("Annually on {{monthDay}}", { monthDay: moment(state?.start_date)?.format("MMMM D") }),
                      },
                      { id: "every_week_day", label: t("Every weekday (Monday to Friday)") },
                      { id: "custom", label: t("Custom") },
                    ]}
                  />
                </FormLayout.Group>
              </>
            ) : null}
            {state?.recurrence_type === "custom" && state?.page === "tee-sheet" ? (
              <>
                <FormLayout.Group>
                  <Input
                    label={t("Repeat every")}
                    type="number"
                    value={state.repeat_amount}
                    onChange={e => setState(prev => ({ ...prev, repeat_amount: e.target.value }))}
                    placeholder={"1"}
                  />
                  <SelectNew
                    id={"interval"}
                    label={t("")}
                    labelHidden
                    placeholder={t("Day")}
                    onChange={interval => setState(prev => ({ ...prev, interval: interval }))}
                    value={state.interval}
                    options={[
                      { id: "day", label: t("Day") },
                      { id: "week", label: t("Week") },
                      { id: "month", label: t("Month") },
                      { id: "year", label: t("Year") },
                    ]}
                    usePortal
                  />
                </FormLayout.Group>
                {state?.interval === "week" ? (
                  <FormLayout.Group>
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Sunday = 0
                                        ...
                                        Saturday = 6 */}
                      {moment?.weekdays()?.map((weekDay, index) => {
                        return (
                          <div key={index} className="grow-0">
                            <Checkbox
                              size="small"
                              selectionOutline
                              checked={state?.weekdays?.includes(index)}
                              label={weekDay?.slice(0, 3)}
                              onChange={() => handleDayOfWeekSelection(index)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </FormLayout.Group>
                ) : state?.interval === "month" ? (
                  <FormLayout.Group>
                    <ButtonGroup
                      onButtonClick={(key: "day_of_month" | "nth_weekday") =>
                        setState(prev => ({ ...prev, monthly_rule_type: key }))
                      }
                      startKey={state?.monthly_rule_type}
                      buttons={[
                        {
                          label: t("Monthly on {{day}}{{ending}}", {
                            day: state?.start_date?.getDate(),
                            ending:
                              state?.start_date?.getDate() === 1
                                ? "st"
                                : state?.start_date?.getDate() === 2
                                ? "nd"
                                : state?.start_date?.getDate() === 3
                                ? "rd"
                                : "th",
                          }),
                          key: "day_of_month",
                        },
                        {
                          label: t("Monthly on {{ordinalName}} {{dayOfWeek}}", {
                            dayOfWeek: moment(state?.start_date)?.format("dddd"),
                            ordinalName: ORDINALS[weekIndex - 1] ?? t("last"),
                          }),
                          key: "nth_weekday",
                        },
                      ]}
                    />
                  </FormLayout.Group>
                ) : null}
                <FormLayout.Group>
                  <p className="ui-events-calendar-label">{t("Ends")}</p>
                </FormLayout.Group>
                <FormLayout.Group alignCenter>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      size="small"
                      circle
                      label={t("On")}
                      checked={state?.ends_type === "on"}
                      onChange={() => setState(prev => ({ ...prev, ends_type: "on" }))}
                    />
                    <div style={{ width: "100%" }}>
                      <DatePickerInput
                        months={1}
                        position="left"
                        label={""}
                        startingDate={state.ends_on_date}
                        setStartingDate={date => setState(prev => ({ ...prev, ends_on_date: date as Date }))}
                        disabled={state?.ends_type !== "on"}
                        verticalPosition="top"
                        // usePortal
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      size="small"
                      circle
                      label={t("After")}
                      checked={state?.ends_type === "after"}
                      onChange={() => setState(prev => ({ ...prev, ends_type: "after" }))}
                    />
                    <div style={{ width: "100%" }}>
                      <Input
                        value={state?.ends_after_occurrences}
                        onChange={e => setState(prev => ({ ...prev, ends_after_occurrences: e.target.value }))}
                        inputRef={afterInputRef}
                        labelHidden
                        placeholder={"1"}
                        type="number"
                        suffix={t("time(s)")}
                        disabled={state?.ends_type !== "after"}
                      />
                    </div>
                  </div>
                </FormLayout.Group>
              </>
            ) : null}
          </FormLayout>
        ) : (
          <FormLayout>
            <FormLayout.Group>
              {state?.page === "tee-sheet" ? (
                <DatePickerInput
                  months={1}
                  position="left"
                  label={t("Start date")}
                  startingDate={state.start_date}
                  setStartingDate={date => setState(prev => ({ ...prev, start_date: date as Date }))}
                  usePortal
                />
              ) : (
                <SelectNew
                  id={"template"}
                  label={t("Tee sheet template")}
                  placeholder={t("Select template")}
                  onChange={templateId =>
                    setState(prev => ({
                      ...prev,
                      template_id: templateId,
                      course_id: templates?.find(template => template?.id === templateId)?.course_id,
                    }))
                  }
                  value={state.template_id}
                  isLoading={!templates}
                  options={templates?.map(template => ({ id: template.id, label: template.name }))}
                  disabled={!!application}
                  usePortal
                />
              )}

              {state?.recurrence_type === "custom" ||
              state?.recurrence_type === "does_not_repeat" ||
              state?.page === "template" ? null : (
                <DatePickerInput
                  months={1}
                  position="right"
                  label={t("End date")}
                  startingDate={state.end_date}
                  setStartingDate={date => setState(prev => ({ ...prev, end_date: date as Date }))}
                  usePortal
                />
              )}
            </FormLayout.Group>
            <FormLayout.Group>
              {facilityStore?.facility?.courses?.length > 1 && state?.page === "tee-sheet" ? (
                <SelectNew
                  id={"courses"}
                  label={t("Course")}
                  placeholder={t("Select course")}
                  onChange={courseId => setState(prev => ({ ...prev, course_id: courseId }))}
                  value={state.course_id}
                  isLoading={!facilityStore?.facility?.courses}
                  options={facilityStore?.facility?.courses?.map(course => ({
                    id: course.id,
                    label: course?.full_name,
                  }))}
                  disabled={!!application}
                />
              ) : null}

              {state?.course_id ? (
                <SelectNew
                  id={"divisions"}
                  label={t("Division")}
                  placeholder={t("Select division")}
                  onChange={divisionId => setState(prev => ({ ...prev, division_id: divisionId }))}
                  value={state.division_id}
                  isLoading={!facilityStore?.facility?.courses}
                  options={facilityStore?.facility?.courses
                    ?.find(course => course.id === state?.course_id)
                    ?.divisions?.map(division => ({ id: division.id, label: division?.title }))}
                  disabled={!!application}
                />
              ) : null}
            </FormLayout.Group>
            {!application || (application && renderTimePick) ? (
              <FormLayout.Group>
                <TimePick
                  label={t("Start time")}
                  value={state.start_time}
                  size="large"
                  onChange={time => setState(prev => ({ ...prev, start_time: time }))}
                />
                <TimePick
                  label={t("End time")}
                  value={state.end_time}
                  size="large"
                  onChange={time => setState(prev => ({ ...prev, end_time: time }))}
                />
              </FormLayout.Group>
            ) : null}
            {state?.page === "template" ? (
              <>
                <FormLayout.Group>
                    <div className="ui-events-calendar-divider"></div>
                  </FormLayout.Group>
                <FormLayout.Group alignCenter>
                  <Checkbox label={t("Set as a shotgun event")} onChange={e => setIsShotgun(e.target?.checked)} checked={isShotgun} size="medium"/>
                </FormLayout.Group>
              </>
            ) : null}
            {state?.page === "tee-sheet" ? (
              <>
              <FormLayout.Group>
                  <div className="ui-events-calendar-divider"></div>
                </FormLayout.Group>
              <FormLayout.Group alignCenter>
                <Checkbox label={t("Set as a shotgun event")} onChange={e => setIsShotgun(e.target?.checked)} checked={isShotgun} size="medium"/>
              </FormLayout.Group>
              <FormLayout.Group>
                  <div className="ui-events-calendar-divider"></div>
              </FormLayout.Group>
              <FormLayout.Group>
                <SelectNew
                  id={"repeat_selection"}
                  label={t("")}
                  labelHidden
                  placeholder={t("Select Time")}
                  onChange={recurrence_type => setState(prev => ({ ...prev, recurrence_type: recurrence_type }))}
                  value={state.recurrence_type}
                  usePortal
                  options={[
                    { id: "does_not_repeat", label: t("Does not repeat") },
                    { id: "daily", label: t("Daily") },
                    {
                      id: "weekly",
                      label: t("Weekly on {{dayOfWeek}}", { dayOfWeek: moment(state?.start_date)?.format("dddd") }),
                    },
                    {
                      id: "monthly",
                      label: t("Monthly on first {{dayOfWeek}}", {
                        dayOfWeek: moment(state?.start_date)?.format("dddd"),
                      }),
                    },
                    {
                      id: "annually",
                      label: t("Annually on {{monthDay}}", { monthDay: moment(state?.start_date)?.format("MMMM D") }),
                    },
                    { id: "every_week_day", label: t("Every weekday (Monday to Friday)") },
                    { id: "custom", label: t("Custom") },
                  ]}
                />
              </FormLayout.Group>
              </>
            ) : null}

            {state?.recurrence_type === "custom" && state?.page === "tee-sheet" ? (
              <>
                <FormLayout.Group>
                  <Input
                    label={t("Repeat every")}
                    type="number"
                    value={state.repeat_amount}
                    onChange={e => setState(prev => ({ ...prev, repeat_amount: e.target.value }))}
                    placeholder={"1"}
                  />
                  <SelectNew
                    id={"interval"}
                    label={t("")}
                    labelHidden
                    placeholder={t("Day")}
                    onChange={interval => setState(prev => ({ ...prev, interval: interval }))}
                    value={state.interval}
                    options={[
                      { id: "day", label: t("Day") },
                      { id: "week", label: t("Week") },
                      { id: "month", label: t("Month") },
                      { id: "year", label: t("Year") },
                    ]}
                    usePortal
                  />
                </FormLayout.Group>
                {state?.interval === "week" ? (
                  <FormLayout.Group>
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Sunday = 0
                                        ...
                                        Saturday = 6 */}
                      {moment?.weekdays()?.map((weekDay, index) => {
                        return (
                          <div key={index} className="grow-0">
                            <Checkbox
                              size="small"
                              selectionOutline
                              checked={state?.weekdays?.includes(index)}
                              label={weekDay?.slice(0, 3)}
                              onChange={() => handleDayOfWeekSelection(index)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </FormLayout.Group>
                ) : state?.interval === "month" ? (
                  <FormLayout.Group>
                    <ButtonGroup
                      onButtonClick={(key: "day_of_month" | "nth_weekday") =>
                        setState(prev => ({ ...prev, monthly_rule_type: key }))
                      }
                      startKey={state?.monthly_rule_type}
                      buttons={[
                        {
                          label: t("Monthly on {{day}}{{ending}}", {
                            day: state?.start_date?.getDate(),
                            ending:
                              state?.start_date?.getDate() === 1
                                ? "st"
                                : state?.start_date?.getDate() === 2
                                ? "nd"
                                : state?.start_date?.getDate() === 3
                                ? "rd"
                                : "th",
                          }),
                          key: "day_of_month",
                        },
                        {
                          label: t("Monthly on {{ordinalName}} {{dayOfWeek}}", {
                            dayOfWeek: moment(state?.start_date)?.format("dddd"),
                            ordinalName: ORDINALS[weekIndex - 1] ?? t("last"),
                          }),
                          key: "nth_weekday",
                        },
                      ]}
                    />
                  </FormLayout.Group>
                ) : null}
                <FormLayout.Group>
                  <p className="ui-events-calendar-label">{t("Ends")}</p>
                </FormLayout.Group>
                <FormLayout.Group alignCenter>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      size="small"
                      circle
                      label={t("On")}
                      checked={state?.ends_type === "on"}
                      onChange={() => setState(prev => ({ ...prev, ends_type: "on" }))}
                    />
                    <div style={{ width: "100%" }}>
                      <DatePickerInput
                        months={1}
                        position="left"
                        label={""}
                        startingDate={state.ends_on_date}
                        setStartingDate={date => setState(prev => ({ ...prev, ends_on_date: date as Date }))}
                        disabled={state?.ends_type !== "on"}
                        // verticalPosition="top"
                        usePortal
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      size="small"
                      circle
                      label={t("After")}
                      checked={state?.ends_type === "after"}
                      onChange={() => setState(prev => ({ ...prev, ends_type: "after" }))}
                    />
                    <div style={{ width: "100%" }}>
                      <Input
                        value={state?.ends_after_occurrences}
                        onChange={e => setState(prev => ({ ...prev, ends_after_occurrences: e.target.value }))}
                        inputRef={afterInputRef}
                        labelHidden
                        placeholder={"1"}
                        type="number"
                        suffix={t("time(s)")}
                        disabled={state?.ends_type !== "after"}
                      />
                    </div>
                  </div>
                </FormLayout.Group>
              </>
            ) : null}
          </FormLayout>
        )}
      </Sheet>
    </Portal>
  );
}
